import {
  getAddressStringForConsumer,
  useCustomFilters,
} from "@features/cx/shared/utils/filters";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useServiceRequests, useServiceRequestsExport } from "../../hooks";
import { ServiceRequest, ServiceRequestFilters } from "../../types";
import { FilterPanel } from "../filters";

const columns: TableColumn<ServiceRequest>[] = [
  { key: "requestId", header: "Service Request ID", type: "text" },
  { key: "serviceType", header: "Service Type", type: "text" },
  {
    key: "customerName",
    header: "Consumer",
    render: (value, row) => (
      <div>
        <p className="font-medium">{row.customerName}</p>
        <p className="text-xs text-muted-foreground">{row.accountNumber}</p>
      </div>
    ),
  },
  {
    key: "supportRequestType",
    header: "Category/Sub Category",
    render: (value, row) => (
      <div>
        <p className="font-medium">{row.supportRequestType ?? "NA"}</p>
        <p className="text-xs text-muted-foreground">
          {row.supportRequestSubtype ?? "NA"}
        </p>
      </div>
    ),
  },
  {
    key: "status",
    header: "Status",
    type: "badge",
    badgeConfig: {
      getVariant: (value: string) => {
        value = value.toLowerCase();
        if (value === "pending") return "secondary";
        if (value === "scheduled") return "outline";
        if (value === "in progress") return "default";
        if (value === "completed") return "success";
        if (value === "canceled") return "destructive";
        if (value === "rejected") return "destructive";
        if (value === "approved") return "success";
        if (value === "pending approval") return "secondary";
        if (value === "pending payment") return "secondary";
        if (value === "pending approval") return "secondary";
        return "default";
      },
    },
  },
  {
    key: "createdDate",
    header: "Created On",
    render: (value, row) => (
      <div>
        <p className="font-medium">{row.createdDate ?? "NA"}</p>
        <p className="text-xs text-muted-foreground">{row.createdBy ?? "NA"}</p>
      </div>
    ),
  },
];

export const HistoryList: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();

  // Memoize the configuration to prevent infinite re-renders
  const filterConfig = useMemo(
    () => ({
      paramNames: {
        status: "status",
        category: "category",
        search: "search_data",
        page: "page",
      },
      defaults: {
        page: 1,
        limit: PAGE_SIZE,
        status: [],
        category: "ALL_CATEGORIES",
        search: "",
      },
    }),
    []
  );

  // Use the configurable URL filter utility
  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  const exportFilters: ServiceRequestFilters = useMemo(() => {
    return {
      remoteUtilityId: Number(remoteUtilityId),
      activeTab: "History",
      requestType: "Service",
      searchData: filters.search_data,
      status: filters.status?.length ? filters.status.join(",") : undefined,
      name: filters.name,
    };
  }, [filters, remoteUtilityId]);

  const { exportCSV, isExporting } = useServiceRequestsExport(exportFilters);

  const handleExport = () => {
    exportCSV();
  };

  // Use the hook to get history data with filters
  const { data } = useServiceRequests({
    remoteUtilityId: Number(remoteUtilityId),
    activeTab: "History",
    page,
    limit: PAGE_SIZE,
    requestType: "Service",
    searchData: filters.search_data,
    status: filters.status?.length ? filters.status.join(",") : undefined,
  });

  const actions: TableAction<ServiceRequest>[] = [
    {
      label: "View Details",
      onClick: (item) => {
        navigate(`/cx/services/history/${item.id}`);
      },
    },
  ];

  // Transform API data to match the table structure
  const transformedData: ServiceRequest[] =
    data?.results?.map((item: any) => ({
      id: item.id.toString(),
      requestId: item.requestNo,
      serviceType: item.utilitySupportRequest.name,
      customerName: `${item.firstName} ${item.lastName}`,
      accountNumber: item.consumerNo,
      serviceAddress: getAddressStringForConsumer(item.serviceAddress?.service),
      status: (item.statusDisplay?.toLowerCase() ||
        "pending") as ServiceRequest["status"],
      createdDate: item.createdDate,
      createdBy: item.createdUserRemoteName,
      approvalDate: item.lastModifiedDate,
      approver: item.lastModifiedUserRemoteName,
      paymentStatus: "unpaid" as const, // This might need to come from additionalData
      estimatedFee: item.additionalData?.estTotalCost || 0,
      supportRequestType: item.supportRequestType,
      supportRequestSubtype: item.supportRequestSubtype,
    })) || [];

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const historyTab = 1;

  return (
    <GenericTable
      data={transformedData}
      columns={columns}
      rowKey="id"
      searchPlaceholder="Search by name, email, or ID..."
      searchParamName="query"
      searchDebounceMs={300}
      onSearch={handleSearch}
      onExport={handleExport}
      exportLoading={isExporting}
      pagination={{
        currentPage: page,
        totalPages,
        totalItems,
        itemsPerPage: PAGE_SIZE,
        startItem: (page - 1) * PAGE_SIZE + 1,
        endItem: Math.min(page * PAGE_SIZE, totalItems),
      }}
      onPageChange={handlePageChange}
      actions={actions}
      actionsType="dropdown"
      filterPanel={
        <FilterPanel
          currentFilters={filters}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          tab={historyTab}
        />
      }
    />
  );
};
